import Cookies from 'js-cookie';
import { getConfig } from '../../configProvider';
import { sleep } from '../../utils';
import { getEnvPrefix } from '../../utils/env';
import eventBus from '../../utils/eventBus';
import { getWPSPayStatus, getWPSUserInfo, wpsOAuth2Login } from './api';
import {
    buyMerchandiseByBillno,
    callWpsClientLogin,
    callWpsWebLoadFinish,
    createWPSClientPayOrder,
    getLaunchOptions,
    getWPSClientUserInfo,
    initWPSInsertImageListeners,
    isFromWPSClient,
    isFromWPSClientOpenImage,
    offLoginStatusChanged,
    onWPSLoginStatusChanged,
    waitWPSClientReady,
    wpsClientIsLogin,
} from './client';
import {
    WPS_BIND_INFO_UPDATED,
    WPS_LOGIN_SUCCESS,
    WPS_LOGOUT_EVENT,
    WPS_PAY_MODAL_LISTENER_EVENT_CLOSE,
    WPS_PAY_MODAL_LISTENER_EVENT_SHOW,
    WPS_PAY_MODAL_LISTENER_EVENT_SUCCESS,
} from './constant';
import { isServerSide } from './runtime';
import { syncWpsSourceParamsFromUrl } from './source';
import { loadWpsDwSdk, setWpsTrackCommonParams } from './track';
import { OpenWpsPayModalOptions, WPSPayResult } from './type';
import { getWPSPayInfo, isFromWPSWeb, redirectToWPS } from './web';

export {
    isFromWPSClient,
    isFromWPSClientOpenImage,
    isFromWPSClientOpenImageEdit,
    getWPSSelectImageBase64,
} from './client';
export { isFromWPSWeb } from './web';
export { appendWpsSourceParamsToUrl, syncWpsSourceParamsFromUrl } from './source';
export { loadWpsDwSdk, trackWpsEvent } from './track';

const DEFAULT_WPS_PAY_POLL_INTERVAL = 1000;
const DEFAULT_WPS_PAY_POLL_TIMEOUT = 20 * 60 * 1000;
const WPS_CLIENT_PAY_STATUS_SYNC_TIMEOUT = 10 * 1000;

let wpsClientLoggedIn = false;

type PollWpsPayStatusOptions = OpenWpsPayModalOptions & {
    shouldStop?: () => boolean;
};

function emitWpsPaySuccess(result: WPSPayResult, trackParams?: any) {
    eventBus.emit(WPS_PAY_MODAL_LISTENER_EVENT_SUCCESS, {
        result,
        trackParams,
    });
}

async function pollWpsWebPayStatus(billNo: string, options: PollWpsPayStatusOptions = {}) {
    const pollInterval = options.pollInterval ?? DEFAULT_WPS_PAY_POLL_INTERVAL;
    const pollTimeout = options.pollTimeout ?? DEFAULT_WPS_PAY_POLL_TIMEOUT;
    const startTime = Date.now();
    let closed = false;

    const handleClose = () => {
        closed = true;
    };

    eventBus.on(WPS_PAY_MODAL_LISTENER_EVENT_CLOSE, handleClose);

    try {
        while (!closed && !options.shouldStop?.() && Date.now() - startTime <= pollTimeout) {
            const result = await getWPSPayStatus(billNo);

            if (result.status) {
                return true;
            }

            if (closed || options.shouldStop?.()) {
                break;
            }

            await sleep(pollInterval);
        }

        return false;
    } finally {
        eventBus.off(WPS_PAY_MODAL_LISTENER_EVENT_CLOSE, handleClose);
    }
}

/**
 * 调用设计室接口获取是否绑定了WPS
 */
export async function getWPSBindInfo() {
    let hasWPSUserInfo = false;
    try {
        // 调用设计室接口获取是否绑定了WPS
        const wpsUserInfo = await getWPSUserInfo();
        console.log('wpsUserInfo ======>', wpsUserInfo);
        hasWPSUserInfo = !!wpsUserInfo.access_token;
        // 同步 open_id 到埋点公共参数；未绑定时下方 catch 会把它清空
        setWpsTrackCommonParams({ open_id: wpsUserInfo.open_id || '' });
    } catch (error) {
        // 未绑定会走到这里，401
        console.log('getWPSUserInfo error ======>', error);
        setWpsTrackCommonParams({ open_id: '' });
    }

    // 通知业务方刷新用户信息（该接口可能涉及赠送权益，需要业务方重新拉取最新用户数据）
    eventBus.emit(WPS_BIND_INFO_UPDATED);

    return hasWPSUserInfo;
}

/**
 * WPS web端登录授权流程
 */
export async function wpsWebLogin() {
    const hasWPSUserInfo = await getWPSBindInfo();

    if (!hasWPSUserInfo) {
        // 未绑定WPS，跳转到WPS去授权登录
        redirectToWPS();
        return false;
    }

    // web 端已绑定 WPS（已有 access_token）= 已是可用登录态，与端内授权成功统一发出登录成功事件，
    // 接入方监听 WPS_LOGIN_SUCCESS 即可，无需区分 client / web。
    eventBus.emit(WPS_LOGIN_SUCCESS);

    return true;
}

async function attemptWPSClientAuth() {
    const res = await getWPSClientUserInfo();
    if (res.code === 0) {
        try {
            await wpsOAuth2Login(res.login_code);
            // 美图授权换取 token 成功 —— 端内登录的最终成功收口点，发出统一登录成功事件。
            // login() 通过监听此事件桥接「真正登录成功」，成功后才继续 loadAccount/登录回调。
            eventBus.emit(WPS_LOGIN_SUCCESS);
        } catch (error) {
            console.error('wpsOAuth2Login failed:', error);
        }
    } else {
        console.log('refuse auth:', res.code);
        // 拒绝授权时，通知业务方同步退出登录状态（不刷新页面）
        // 为了解决授权后，去个人中心取消授权再次进入页面的情况
        eventBus.emit(WPS_LOGOUT_EVENT);
    }
}

/**
 * WPS端内登录授权流程
 */
export async function wpsClientLogin() {
    await waitWPSClientReady();

    // 如果未登录，先唤起登录弹窗
    if (!wpsClientLoggedIn) {
        await callWpsClientLogin();
    } else {
        // 调用授权弹窗获取到code后，触发美图登录
        await attemptWPSClientAuth();
    }

    // 更新权益
    getWPSBindInfo();

    return true;
}

/**
 * 检查是否登录了WPS，否则进行授权登录
 * 客户端和web端流程不同
 * @return {boolean} 是否已完成 WPS 绑定校验
 */
export async function wpsCheckLogin() {
    let hasBindWPS = false;

    if (isFromWPSClient()) {
        // 客户端
        hasBindWPS = await wpsClientLogin();
    }
    if (isFromWPSWeb()) {
        hasBindWPS = await wpsWebLogin();
    }

    return hasBindWPS;
}

export function isFromWPS() {
    return isFromWPSWeb() || isFromWPSClient();
}

/**
 * 打开WPS支付弹窗
 */
export async function openWpsPayModal(trackParams?: any, options: OpenWpsPayModalOptions = {}): Promise<WPSPayResult> {
    console.log('openWpsPayModal: trackParams ======>', trackParams);
    if (isFromWPSClient()) {
        let stopPayStatusPolling = false;
        const preOrder = await createWPSClientPayOrder(trackParams?.subscribe_type || '');
        const billNo = preOrder.bill_no;

        // 内嵌不需要打开弹出，但是需要上报事件
        eventBus.emit(WPS_PAY_MODAL_LISTENER_EVENT_SHOW, {
            show: false,
            url: '',
            trackParams,
        });

        const payStatusPromise = pollWpsWebPayStatus(billNo, {
            ...options,
            shouldStop: () => stopPayStatusPolling,
        });
        payStatusPromise.catch((error) => {
            console.log('WPS客户端支付状态轮询异常 ======>', error);
        });

        const payResult = await buyMerchandiseByBillno(billNo);
        const status = payResult.data.status;

        console.log('WPS客户端支付结果 ======>', status, billNo);

        // 客户端支付接口返回后，最多再给服务端 10 秒同步支付状态；若轮询已成功则立即返回。
        const pollStatus = Boolean(
            await Promise.race([
                payStatusPromise,
                sleep(WPS_CLIENT_PAY_STATUS_SYNC_TIMEOUT).then(async () => {
                    stopPayStatusPolling = true;
                    return payStatusPromise;
                }),
            ]),
        );

        console.log('pollStatus:  ======>', pollStatus);

        const result: WPSPayResult = {
            status: pollStatus,
            url: '',
        };

        if (pollStatus) {
            emitWpsPaySuccess(result, trackParams);
            options.onSuccess?.(result);
        }

        return result;
    }

    if (isFromWPSWeb()) {
        const { url, billNo } = await getWPSPayInfo(trackParams.subscribe_type || '');

        if (!url) {
            return {
                status: false,
                url: '',
                billNo,
            };
        }

        eventBus.emit(WPS_PAY_MODAL_LISTENER_EVENT_SHOW, {
            show: true,
            url,
            trackParams,
        });

        const status = await pollWpsWebPayStatus(billNo, options);
        const result: WPSPayResult = {
            status,
            url,
            billNo,
        };

        if (result.status) {
            emitWpsPaySuccess(result, trackParams);
            options.onSuccess?.(result);
        }

        return result;
    }

    return {
        status: false,
        url: '',
    };
}

/**
 * 获取 WPS access token
 * @returns WPS access token
 */
export function getWPSAccessToken() {
    const envPrefix = getEnvPrefix(getConfig().env);
    const cookieName = `__mt${envPrefix ? `_${envPrefix}` : ''}_wps_access_token__`;

    if (isServerSide()) return '';

    return Cookies.get(cookieName);
}

/**
 * WPS 端内中间页专用：等待客户端就绪并读取 launchOptions 下发的 redirect_url。
 * 仅用于路由分发，不包含任何业务 / 登录 / 埋点初始化逻辑。
 * @param timeout - 兜底超时（毫秒），超时后返回默认路径，避免卡在 loading 页
 * @returns 目标相对路径（含 path 与 query）；未下发 redirect_url 时返回 '/'
 */
export async function resolveWpsLaunchRedirectUrl(timeout = 3000): Promise<string> {
    const DEFAULT_REDIRECT_URL = '/';

    if (isServerSide()) return DEFAULT_REDIRECT_URL;

    const resolveFromClient = async () => {
        await waitWPSClientReady();
        const launchOptions = await getLaunchOptions();
        return launchOptions?.data?.extraData?.redirect_url || DEFAULT_REDIRECT_URL;
    };

    try {
        return await Promise.race([resolveFromClient(), sleep(timeout).then(() => DEFAULT_REDIRECT_URL)]);
    } catch (error) {
        console.error('resolveWpsLaunchRedirectUrl failed:', error);
        return DEFAULT_REDIRECT_URL;
    }
}

/**
 * WPS 相关初始化逻辑
 * web 进入页面就强制 wps 登录
 * 内嵌 每次进入时都弹出授权弹窗，允许关闭
 */
export async function wpsInit(options?: { moduleId?: string }) {
    syncWpsSourceParamsFromUrl();
    // 初始化 WPS DW 埋点 SDK
    loadWpsDwSdk();

    if (options?.moduleId) {
        setWpsTrackCommonParams({ module_id: options.moduleId });
    }

    let client = 'web';

    // web 端 position 来自 URL query，命中较早（不依赖 WPS 客户端就绪），先写入；
    // 端内 position 在下方 waitWPSClientReady 后用 launchOptions 覆盖。
    if (!isServerSide() && isFromWPSWeb()) {
        const queryParams = new URLSearchParams(window.location.search);
        const position = queryParams.get('position') || 'docertest';
        setWpsTrackCommonParams({ position, client });
    }

    const hasWPSUserInfo = await getWPSBindInfo();

    if (isFromWPSWeb() && !hasWPSUserInfo) {
        // web 端未绑定WPS，跳转到WPS去授权登录
        redirectToWPS();
        return;
    }

    //  内嵌初始化逻辑
    if (isFromWPSClient()) {
        await waitWPSClientReady();

        client = 'PC';
        const ua = window?.navigator?.userAgent;
        if (ua?.includes('Mac') || ua?.includes('mac')) {
            client = 'Mac';
        }

        // 端内 position 来自 WPS 客户端 launchOptions，与 createWPSClientPayOrder 复用同一来源与 fallback。
        try {
            const options = await getLaunchOptions();
            const position = options?.data?.extraData?.position || 'docertest';
            setWpsTrackCommonParams({ position, client });
        } catch (error) {
            console.error('getLaunchOptions for track failed:', error);
            setWpsTrackCommonParams({ position: 'docertest', client });
        }

        if (isFromWPSClientOpenImage()) {
            callWpsWebLoadFinish();
            initWPSInsertImageListeners();
        }

        // 注销事件监听
        offLoginStatusChanged();

        // 监听WPS登录状态变化事件，登录状态变化时同步更新设计室登录状态
        onWPSLoginStatusChanged(async (isLogin) => {
            wpsClientLoggedIn = isLogin;
            console.log('wps login status changed, isLogin:', isLogin);
            if (!isLogin) {
                // WPS退出账号
                console.log('wps logout success');
                // 通知业务方同步退出登录状态（不刷新页面）
                eventBus.emit(WPS_LOGOUT_EVENT);
            } else {
                // WPS登录成功，调用授权，触发美图登录
                console.log('wps login success');
                await attemptWPSClientAuth();
                await getWPSBindInfo();
            }
        });

        // 初始化时主动查询一次当前登录状态
        try {
            wpsClientLoggedIn = await wpsClientIsLogin();
        } catch (error) {
            console.error('wpsClientIsLogin failed:', error);
        }

        // 如果未登录，先唤起登录弹窗
        if (!wpsClientLoggedIn) {
            await callWpsClientLogin();
        } else {
            // 已经登录了 WPS
            // 调用授权弹窗获取到code后，触发美图登录
            await attemptWPSClientAuth();
            await getWPSBindInfo();
        }
    }
}
